import React, { useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import ReactToPrint from "react-to-print";
import { Client } from "../../../interfaces/ClientInterface";
import { Training } from "../../../interfaces/TrainingInterface";
import usePrintStyles from "../../../hooks/usePrintStyles";
import { ResourceCentre } from "../../../interfaces/ResourceCentreInterface";
import Qr from "../../../components/Qr/Qr";

/* eslint-disable @typescript-eslint/no-var-requires */
const topOrnament = require("../../../../assets/images/top-ornament.png");
const bottomOrnament = require("../../../../assets/images/bottom-ornament.png");
const dividerOrnament = require("../../../../assets/images/divider.png");
const ribbonOrnament = require("../../../../assets/images/ribbon.png");
/* eslint-enable @typescript-eslint/no-var-requires */

const pageStyle = `
  @page {
    size: A4 landscape;
    margin: 0;
  }

  @media print {
    html,
    body {
      width: 297mm;
      height: 210mm;
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }\n  }\n`;

const formatDate = (date?: string | Date, withOrdinal = true) => {
  if (!date) return "";

  const parsedDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";

  const day = parsedDate.getDate();
  const month = parsedDate.toLocaleString("en-GB", { month: "long" });
  const year = parsedDate.getFullYear();

  if (withOrdinal) {
    const s = ["th", "st", "nd", "rd"];
    const v = day % 100;
    const ord = s[(v - 20) % 10] || s[v] || s[0];
    return `${day}${ord} ${month}, ${year}`;
  }

  return `${day.toString().padStart(2, "0")} ${month}, ${year}`;
};

const formatDuration = (duration?: Training["duration"]) => {
  if (!duration) return "";

  return `${duration.value} ${duration.unit}`.toUpperCase();
};

const defaultSignatories = [
  { order: 1, role: "Trainer", name: "" },
  { order: 2, role: "Program Coordinator", name: "" },
  { order: 3, role: "Managing Director", name: "" }
];

// Template component
const Template1: React.FC<{
  client: Client;
  training: Training;
  resourceCentre?: ResourceCentre;
  trigger?: React.ReactElement;
  onPrintClick?: () => void;
  showInline?: boolean;
}> = ({ client, training, trigger, onPrintClick, resourceCentre, showInline = false }) => {
  const printRef = useRef<HTMLDivElement>(null);

  const printStyles = usePrintStyles({
    pageStyle: "size: A4 landscape; margin: 0mm;"
  });

  printStyles.runPrintStyles();

  // client info
  const clientImageUrl =
    client?.customerDetails?.profileImageS3Url ||
    (typeof client?.image === "string" ? client.image : "");
  const clientRegistrationNo = client.registrationNo ?? "-";
  const clientName = [client?.firstName, client?.lastName].filter(Boolean).join(" ");
  const clientPassportAndCitizenshipNo = (() => {
    const passport = client?.passportInfo?.passportNumber?.trim();
    const citizenship = client?.citizenshipNumber?.trim();
    if (passport && citizenship) return `${passport} / ${citizenship}`;
    return passport || citizenship || "-";
  })();

  const trainingDurationText = training?.duration?.value
    ? `${training.duration.value} ${
        training.duration.unit?.toLowerCase() === "hours" ? "hrs" : training.duration.unit || "hrs"
      }`
    : "-";

  // rc info
  const rcName = resourceCentre?.name || "";
  const rcWebsite = resourceCentre?.web?.trim() || "";
  const rcLogoUrl = resourceCentre?.resourceCentreLogo?.s3ResourceURL || "";
  const rcShortName = resourceCentre?.shortName ? `(${resourceCentre?.shortName})` : "";
  const rcAddress = [resourceCentre?.city, resourceCentre?.address].filter(Boolean).join(", ");

  // verification qr url & requirements
  const certNumber = training?.certificateNumber?.trim() || "";

  const missingQrItems: string[] = [];
  if (!rcWebsite) missingQrItems.push("Website");
  if (!certNumber) missingQrItems.push("Certificate No.");

  const qrVerificationUrl = (() => {
    if (missingQrItems.length > 0) return "";

    const trimmedWeb = rcWebsite.replace(/\/+$/, "");
    const baseWithProtocol = /^https?:\/\//i.test(trimmedWeb)
      ? trimmedWeb
      : `https://${trimmedWeb}`;

    return `${baseWithProtocol}/verify-certificate/?certificate-number=${encodeURIComponent(
      certNumber
    )}`;
  })();

  const qrPlaceholderText =
    missingQrItems.length > 0 ? `${missingQrItems.join(" & ")} Not Provided` : "";

  // signatories
  const configuredSignatories = [...(training?.signatories ?? [])].sort(
    (a, b) => a.order - b.order
  );
  const signatories = configuredSignatories.length > 0 ? configuredSignatories : defaultSignatories;

  if (!training || !resourceCentre || !client) return null;

  return (
    <>
      <ReactToPrint
        trigger={() => trigger ?? <Button variant="contained">Print Certificate</Button>}
        content={() => printRef.current}
        pageStyle={pageStyle}
        onBeforePrint={() => {
          printStyles.applyPrintStyles();
          onPrintClick?.();
        }}
      />

      <Box
        sx={
          showInline
            ? {
                display: "flex",
                justifyContent: "center",
                my: 2,
                overflowX: "auto",
                pb: 2
              }
            : {
                position: "absolute",
                left: "-99999px",
                top: 0
              }
        }
      >
        <Box
          ref={printRef}
          sx={{
            pt: 9.5,
            width: "297mm",
            height: "210mm",
            minWidth: "297mm",
            minHeight: "210mm",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
            backgroundColor: "#fff",
            color: "#162b50",
            fontFamily: '"Times New Roman", Times, Georgia, serif',
            ...(showInline && {
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
            })
          }}
        >
          {/* Top Frame Ornament */}
          <Box
            component="img"
            src={topOrnament}
            alt=""
            sx={{
              position: "absolute",
              top: "5mm",
              left: "50%",
              transform: "translateX(-50%)",
              width: "285mm",
              height: "auto",
              pointerEvents: "none",
              zIndex: 1
            }}
          />

          {/* Bottom Frame Ornament */}
          <Box
            component="img"
            src={bottomOrnament}
            alt=""
            sx={{
              position: "absolute",
              bottom: "5mm",
              left: "50%",
              transform: "translateX(-50%)",
              width: "285mm",
              height: "auto",
              pointerEvents: "none",
              zIndex: 1
            }}
          />

          {/* Header */}
          <Box
            sx={{
              px: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            {/* RC Logo */}
            <Box
              sx={{
                width: "50mm",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start"
              }}
            >
              {rcLogoUrl ? (
                <Box
                  component="img"
                  src={rcLogoUrl}
                  alt="Logo"
                  sx={{
                    maxHeight: "22mm",
                    maxWidth: "50mm",
                    objectFit: "contain"
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "20mm",
                    height: "20mm",
                    border: "0.5mm dashed #aaa",
                    borderRadius: "1mm",
                    boxSizing: "border-box"
                  }}
                />
              )}
            </Box>

            {/* RC Detail */}
            <Box
              sx={{
                flex: 1,
                textAlign: "center"
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontWeight: 700,
                  fontSize: "7.3mm",
                  color: "#162b50",
                  letterSpacing: "0.2mm",
                  textTransform: "uppercase",
                  lineHeight: "1.4"
                }}
              >
                {rcName}
              </Typography>

              {rcShortName && (
                <Typography
                  sx={{
                    fontFamily: '"Times New Roman", Times, Georgia, serif',
                    fontWeight: 700,
                    fontSize: "7.3mm",
                    lineHeight: "1.2",
                    color: "#162b50"
                  }}
                >
                  {rcShortName}
                </Typography>
              )}

              {rcAddress && (
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "3.8mm",
                    color: "#162b50",
                    textTransform: "uppercase"
                  }}
                >
                  {rcAddress}
                </Typography>
              )}

              <Typography
                sx={{
                  my: "0.3mm",
                  fontSize: "3.5mm",
                  color: "#162b50",
                  letterSpacing: "0.1mm"
                }}
              >
                AFFILIATED TO CTEVT
              </Typography>
            </Box>

            {/* Certificate Number */}
            <Box
              sx={{
                width: "50mm"
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "3.3mm",
                  color: "#000000",
                  textAlign: "left"
                }}
              >
                Certification No.
              </Typography>

              <Typography
                sx={{
                  mt: "0.5mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "3.5mm",
                  color: "#000000"
                }}
              >
                {training?.certificateNumber || "-"}
              </Typography>
            </Box>
          </Box>

          {/* Gold Ribbon Banner */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              zIndex: 2,
              pointerEvents: "none",
              position: "relative"
            }}
          >
            <Box
              component="img"
              src={ribbonOrnament}
              alt="CERTIFICATE OF ACHIEVEMENT"
              sx={{
                width: "152mm",
                height: "auto",
                display: "block"
              }}
            />
            <Typography
              sx={{
                position: "absolute",
                top: "45%",
                left: "50%",
                transform: "translate(-50%, -55%)",
                fontFamily: '"Times New Roman", Times, Georgia, serif',
                fontSize: "6.5mm",
                fontWeight: 700,
                color: "#fff"
              }}
            >
              CERTIFICATE OF ACHIEVEMENT
            </Typography>
          </Box>

          {/* Main Certificate Content */}
          <Box
            sx={{
              position: "absolute",
              top: "70mm",
              left: "20mm",
              right: "20mm",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              zIndex: 2
            }}
          >
            <Box sx={{ maxWidth: "165mm", width: "100%" }}>
              <Typography
                sx={{
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "4.4mm",
                  color: "#000000"
                }}
              >
                This is to certify that
              </Typography>

              <Typography
                sx={{
                  mt: "2mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "9.2mm",
                  fontWeight: 700,
                  color: "#162b50",
                  lineHeight: 1.15,
                  letterSpacing: "0.2mm",
                  textTransform: "uppercase"
                }}
              >
                {clientName || "RECIPIENT NAME"}
              </Typography>

              {/* Decorative Divider Ornament */}
              <Box
                component="img"
                src={dividerOrnament}
                alt=""
                sx={{
                  width: "115.5mm",
                  height: "auto",
                  display: "block",
                  mx: "auto",
                  mt: "2mm",
                  mb: "2mm"
                }}
              />

              <Typography
                sx={{
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "4.4mm",
                  color: "#000000"
                }}
              >
                has successfully completed
              </Typography>

              <Typography
                sx={{
                  mt: "1.5mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "9.2mm",
                  lineHeight: 1.15,
                  fontWeight: 700,
                  color: "#162b50",
                  letterSpacing: "0.2mm",
                  textTransform: "uppercase"
                }}
              >
                {training?.name || "CERTIFICATE PROGRAM"}
              </Typography>

              <Typography
                sx={{
                  // mt: "1.5mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "4.8mm",
                  fontWeight: 700,
                  color: "#b89742"
                }}
              >
                ({formatDuration(training?.duration)})
              </Typography>

              <Typography
                sx={{
                  mt: "0.5mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  fontSize: "4.4mm",
                  color: "#000000"
                }}
              >
                from
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column"
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Times New Roman", Times, Georgia, serif',
                    fontSize: "5.2mm",
                    fontWeight: 700,
                    color: "#162b50"
                  }}
                >
                  {rcName}
                </Typography>

                {/* Date of Enrollment & Completion */}
                <Box sx={{ mt: "2mm" }}>
                  <Typography
                    sx={{
                      fontFamily: '"Times New Roman", Times, Georgia, serif',
                      fontSize: "3.8mm",
                      lineHeight: 1.45,
                      color: "#000000"
                    }}
                  >
                    Date of enrollment: {formatDate(training?.startDate) || "-"}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Times New Roman", Times, Georgia, serif',
                      fontSize: "3.8mm",
                      lineHeight: 1.45,
                      color: "#000000"
                    }}
                  >
                    Date of completion: {formatDate(training?.endDate) || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Left Column: Client & Training Meta */}
          <Box
            sx={{
              position: "absolute",
              left: "20mm",
              top: "68mm",
              width: "48mm",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "8mm",
              textAlign: "left",
              zIndex: 2,
              color: "#000000"
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "3.4mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif'
                }}
              >
                Registration No.
              </Typography>
              <Typography
                sx={{
                  mt: "0.8mm",
                  fontSize: "3.7mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif'
                }}
              >
                {clientRegistrationNo}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "3.4mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif'
                }}
              >
                Training Hours {trainingDurationText}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "3.4mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif'
                }}
              >
                Passport / Citizenship No.
              </Typography>
              <Typography
                sx={{
                  mt: "0.8mm",
                  fontSize: "3.7mm",
                  fontFamily: '"Times New Roman", Times, Georgia, serif'
                }}
              >
                {clientPassportAndCitizenshipNo}
              </Typography>
            </Box>
          </Box>

          {/* Right Column: Client Image & Verify Certificate / QR Area */}
          <Box
            sx={{
              position: "absolute",
              right: "20mm",
              top: "52mm",
              width: "38mm",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "3.5mm",
              zIndex: 2
            }}
          >
            {/* Client Photo (Portrait: 34mm x 41mm) */}
            {clientImageUrl ? (
              <Box
                component="img"
                src={clientImageUrl}
                alt={clientName || "Client Photo"}
                sx={{
                  width: "34mm",
                  height: "41mm",
                  objectFit: "cover",
                  border: "0.35mm solid #162b50",
                  borderRadius: "1.5mm",
                  boxSizing: "border-box"
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "34mm",
                  height: "41mm",
                  border: "0.5mm dashed #aaa",
                  borderRadius: "1.5mm",
                  boxSizing: "border-box"
                }}
              />
            )}

            {/* Verify Certificate / QR Area */}
            <Box
              sx={{
                width: "38mm",
                textAlign: "center",
                border: "0.35mm solid #162b50",
                borderRadius: "1.5mm",
                overflow: "hidden",
                boxSizing: "border-box",
                backgroundColor: "#fff"
              }}
            >
              <Box
                sx={{
                  py: "1.2mm",
                  px: "2mm",
                  backgroundColor: "#162b50"
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: "2.4mm",
                    fontWeight: 700,
                    letterSpacing: "0.15mm"
                  }}
                >
                  VERIFY CERTIFICATE
                </Typography>
              </Box>

              <Typography
                sx={{
                  mt: "2mm",
                  px: "1.5mm",
                  fontSize: "2.2mm",
                  color: "#000000",
                  fontFamily: '"Times New Roman", Times, Georgia, serif',
                  lineHeight: 1.3
                }}
              >
                Scan this QR code
                <br />
                to verify the certificate
                <br />
                online.
              </Typography>

              <Box
                sx={{
                  p: 1,
                  border: "1px solid #000000",
                  borderRadius: "1.5mm",
                  mb: "1.5mm",
                  width: "24mm",
                  height: "24mm",
                  mx: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2.2mm",
                  color: "#999",
                  textAlign: "center",
                  lineHeight: 1.2,
                  px: "1mm",
                  boxSizing: "border-box",
                  "& svg": {
                    width: "100%",
                    height: "100%"
                  }
                }}
              >
                {qrVerificationUrl ? <Qr value={qrVerificationUrl} /> : qrPlaceholderText}
              </Box>

              <Box
                sx={{
                  borderTop: "0.3mm solid #162b50"
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#162b50",
                    color: "#fff",
                    py: "0.7mm",
                    px: "1.5mm",
                    textAlign: "left"
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.8mm",
                      color: "#fff",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Visit us at:
                  </Typography>
                </Box>
                <Box
                  sx={{
                    py: "0.8mm",
                    px: "1mm",
                    backgroundColor: "#fff"
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.9mm",
                      color: "#000000",
                      textAlign: "center"
                    }}
                  >
                    Enter Certificate No. to Verify
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Signatories */}
          <Box
            sx={{
              position: "absolute",
              left: "30mm",
              right: "30mm",
              bottom: "25mm",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "flex-end",
              zIndex: 2
            }}
          >
            {signatories.map((signatory) => (
              <Box
                key={`${signatory.order}-${signatory.role}`}
                sx={{
                  width: "48mm",
                  textAlign: "center"
                }}
              >
                {/* Signature space */}
                <Box
                  sx={{
                    height: "13mm"
                  }}
                />

                {/* Signature line */}
                <Box
                  sx={{
                    width: "100%",
                    borderTop: "0.3mm solid #b89742",
                    mb: "1mm"
                  }}
                />

                <Typography
                  sx={{
                    fontSize: "4.8mm",
                    letterSpacing: " 0.1mm",
                    fontFamily: '"Times New Roman", Times, Georgia, serif',
                    color: "#162b50",
                    lineHeight: 1.2
                  }}
                >
                  {signatory.role}
                </Typography>

                {signatory.name && (
                  <Typography
                    sx={{
                      fontSize: "3.5mm",
                      fontFamily: '"Times New Roman", Times, Georgia, serif',
                      color: "#444"
                    }}
                  >
                    {signatory.name}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Issued Date */}
          <Box
            sx={{
              position: "absolute",
              left: "40mm",
              bottom: "18mm",
              zIndex: 2
            }}
          >
            <Typography
              sx={{
                fontSize: "3.2mm",
                fontFamily: '"Times New Roman", Times, Georgia, serif',
                color: "#000000"
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                Issued Date:
              </Box>{" "}
              {formatDate(training?.issueDate) || "-"}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Template1;
